import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import chatWebSocketService from '../services/websocket';
import { AuthContext } from './AuthProvider';
import { createLogger } from '../utils/safeLogger';

const logger = createLogger('WebSocketProvider');

// Context for WebSocket state and methods
const WebSocketContext = createContext();

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

/**
 * WebSocketProvider
 * Centralized WebSocket connection management for real-time updates
 *
 * Features:
 * - Single connection initialization
 * - Automatic reconnection handling
 * - Channel subscription management
 * - Event handler registration
 * - Connection state monitoring
 */
export const WebSocketProvider = ({ children }) => {
  const { user: authData } = useContext(AuthContext);

  // Connection states
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [activeChannels, setActiveChannels] = useState(new Set());

  // Refs for cleanup and state management
  const initializationRef = useRef(false);
  const channelSubscriptions = useRef({});
  const eventHandlers = useRef({});

  // Initialize WebSocket connection once when provider mounts
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      return;
    }

    // Don't initialize if no auth data
    if (!authData || authData === '' || !authData.id) {
      return;
    }

    // Skip WebSocket initialization for admin and sales users
    if (authData.userType === 'admin' || authData.userType === 'sales') {
      console.log('WebSocketProvider: Skipping initialization for non-partner user');
      return;
    }

    initializationRef.current = true;

    // Initialize the core WebSocket service
    try {
      chatWebSocketService.initialize();
      setIsInitialized(true);

      // Check connection status
      const checkConnection = () => {
        const connected = chatWebSocketService.core.isConnected();
        setIsConnected(connected);
        if (connected) {
          setConnectionError(null);
        }
      };

      // Initial check
      setTimeout(checkConnection, 1000);

      // Periodic connection check
      const connectionCheckInterval = setInterval(checkConnection, 5000);

      // Listen for connection failure events
      const handleConnectionFailure = (event) => {
        console.error('WebSocketProvider: Connection failed', event.detail);
        setConnectionError(event.detail);
        setIsConnected(false);
      };

      window.addEventListener('websocket_connection_failed', handleConnectionFailure);

      // Cleanup
      return () => {
        clearInterval(connectionCheckInterval);
        window.removeEventListener('websocket_connection_failed', handleConnectionFailure);
      };
    } catch (error) {
      console.error('WebSocketProvider: Failed to initialize WebSocket', error);
      setConnectionError(error.message);
      setIsInitialized(false);
    }
  }, [authData]);

  /**
   * Subscribe to a WebSocket channel
   * @param {string} channelType - Type of channel (bot, attendant, widget, simulator)
   * @param {Object} params - Channel-specific parameters
   * @param {Object} handlers - Event handlers for the channel
   * @returns {Function} Unsubscribe function
   */
  const subscribeToChannel = useCallback((channelType, params, handlers) => {
    if (!isInitialized) {
      console.warn('WebSocketProvider: Cannot subscribe - WebSocket not initialized');
      return () => {};
    }

    let subscription = null;
    const channelKey = `${channelType}_${JSON.stringify(params)}`;

    // Unsubscribe from existing subscription if any
    if (channelSubscriptions.current[channelKey]) {
      unsubscribeFromChannel(channelType, params);
    }

    try {
      switch (channelType) {
        case 'bot':
          subscription = chatWebSocketService.connectBotConfiguration(
            params.partnerId,
            params.assistantId || null,
            handlers
          );
          break;

        case 'attendant':
          subscription = chatWebSocketService.connectAttendant(
            params.partnerId,
            params.attendantId,
            params.token,
            handlers
          );
          break;

        case 'widget':
          subscription = chatWebSocketService.connectWidget(params.sessionId, handlers);
          break;

        case 'simulator':
          subscription = chatWebSocketService.connectSimulator(
            params.partnerId,
            params.sessionId,
            params.assistantId || null,
            handlers
          );
          break;

        case 'flows':
          // TODO: Integrar flowsWebSocketService
          subscription = { channelType: 'flows', params, handlers };
          break;

        default:
          logger.error(`Unknown channel type: ${channelType}`);
          return () => {};
      }

      if (subscription) {
        // Store subscription and handlers
        channelSubscriptions.current[channelKey] = subscription;
        eventHandlers.current[channelKey] = handlers;

        // Update active channels
        setActiveChannels(prev => new Set([...prev, channelType]));

        // Return unsubscribe function
        return () => unsubscribeFromChannel(channelType, params);
      }
    } catch (error) {
      logger.error(`Failed to subscribe to ${channelType} channel`, error?.message);
    }

    return () => {};
  }, [isInitialized]);

  /**
   * Unsubscribe from a WebSocket channel
   * @param {string} channelType - Type of channel
   * @param {Object} params - Channel-specific parameters
   */
  const unsubscribeFromChannel = useCallback((channelType, params) => {
    const channelKey = `${channelType}_${JSON.stringify(params)}`;

    try {
      switch (channelType) {
        case 'bot':
          chatWebSocketService.disconnectBotConfiguration();
          break;

        case 'attendant':
          chatWebSocketService.disconnectAttendant();
          break;

        case 'widget':
          chatWebSocketService.disconnectWidget();
          break;

        case 'simulator':
          chatWebSocketService.disconnectSimulator();
          break;
      }

      // Clean up stored references
      delete channelSubscriptions.current[channelKey];
      delete eventHandlers.current[channelKey];

      // Update active channels
      const remainingChannels = Object.keys(channelSubscriptions.current)
        .map(key => key.split('_')[0]);
      setActiveChannels(new Set(remainingChannels));

    } catch (error) {
      logger.error(`Error unsubscribing from ${channelType} channel`, error?.message);
    }
  }, []);

  /**
   * Send a message through a specific channel
   * @param {string} channelType - Type of channel
   * @param {string} action - Action to perform
   * @param {Object} data - Data to send
   */
  const sendMessage = useCallback((channelType, action, data) => {
    if (!isInitialized || !isConnected) {
      logger.warn('Cannot send message - WebSocket not connected');
      return Promise.reject(new Error('WebSocket not connected'));
    }

    try {
      switch (channelType) {
        case 'attendant':
          if (action === 'accept_takeover') {
            return chatWebSocketService.acceptTakeover(data.takeoverId, data.attendantId);
          } else if (action === 'send_message') {
            return chatWebSocketService.sendAttendantMessage(
              data.takeoverId,
              data.attendantId,
              data.content
            );
          }
          break;

        case 'simulator':
          if (action === 'send_message') {
            return chatWebSocketService.sendSimulatorMessage(data.message);
          } else if (action === 'reset') {
            return chatWebSocketService.resetSimulatorConversation();
          } else if (action === 'request_state') {
            return chatWebSocketService.requestSimulatorState();
          }
          break;

        case 'bot':
          if (action === 'request_configuration') {
            return chatWebSocketService.requestBotConfiguration();
          }
          break;

        default:
          logger.error(`Unknown channel type for message: ${channelType}`);
          return Promise.reject(new Error(`Unknown channel type: ${channelType}`));
      }
    } catch (error) {
      logger.error(`Error sending message to ${channelType}`, error?.message);
      return Promise.reject(error);
    }
  }, [isInitialized, isConnected]);

  /**
   * Force reconnection of WebSocket
   */
  const reconnect = useCallback(() => {
    // Clear initialization flag
    initializationRef.current = false;

    // Reinitialize
    chatWebSocketService.initialize(true);
    setIsInitialized(true);

    // Resubscribe to all active channels
    setTimeout(() => {
      Object.entries(channelSubscriptions.current).forEach(([channelKey, subscription]) => {
        const [channelType, ...paramsParts] = channelKey.split('_');
        const params = JSON.parse(paramsParts.join('_'));
        const handlers = eventHandlers.current[channelKey];
        subscribeToChannel(channelType, params, handlers);
      });
    }, 1000);
  }, [subscribeToChannel]);

  // Context value
  const value = {
    // State
    isInitialized,
    isConnected,
    connectionError,
    activeChannels: Array.from(activeChannels),

    // Methods
    subscribeToChannel,
    unsubscribeFromChannel,
    sendMessage,
    reconnect,

    // Direct access to service (for advanced usage)
    service: chatWebSocketService
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Custom hook for Bot Configuration channel
 * Uses refs to prevent re-render loops while maintaining WebSocket subscription
 */
export const useBotConfigurationChannel = (partnerId, assistantId, handlers) => {
  const { subscribeToChannel, isConnected } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Use refs to store values without triggering re-renders
  // This prevents infinite loops when handlers change
  const stableRefs = useRef({
    partnerId: partnerId,
    assistantId: assistantId,
    handlers: handlers,
    subscribeToChannel: subscribeToChannel,
    unsubscribe: null,
    hasSubscribed: false
  });

  // Update refs on each render to keep them current
  stableRefs.current.partnerId = partnerId;
  stableRefs.current.assistantId = assistantId;
  stableRefs.current.handlers = handlers;
  stableRefs.current.subscribeToChannel = subscribeToChannel;

  useEffect(() => {
    // Cleanup previous subscription when assistantId changes
    if (stableRefs.current.unsubscribe) {
      stableRefs.current.unsubscribe();
      stableRefs.current.unsubscribe = null;
      stableRefs.current.hasSubscribed = false;
      setIsSubscribed(false);
    }

    // Only subscribe when connected and partnerId is available
    if (!isConnected || !stableRefs.current.partnerId) {
      return;
    }

    // Mark as subscribed immediately to prevent double subscription
    stableRefs.current.hasSubscribed = true;

    // Create the subscription
    const unsubscribe = stableRefs.current.subscribeToChannel(
      'bot',
      { partnerId: stableRefs.current.partnerId, assistantId: stableRefs.current.assistantId },
      stableRefs.current.handlers
    );

    if (unsubscribe && typeof unsubscribe === 'function') {
      stableRefs.current.unsubscribe = unsubscribe;
      setIsSubscribed(true);
    } else {
      // Reset if subscription failed
      stableRefs.current.hasSubscribed = false;
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (stableRefs.current.unsubscribe) {
        stableRefs.current.unsubscribe();
        stableRefs.current.unsubscribe = null;
        stableRefs.current.hasSubscribed = false;
        setIsSubscribed(false);
      }
    };
  }, [isConnected, assistantId]); // Re-subscribe when assistant changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stableRefs.current.unsubscribe) {
        stableRefs.current.unsubscribe();
        stableRefs.current.unsubscribe = null;
        stableRefs.current.hasSubscribed = false;
      }
    };
  }, []); // Empty deps = only on unmount

  return { isSubscribed, isConnected };
};

/**
 * Custom hook for Attendant channel
 */
export const useAttendantChannel = (partnerId, attendantId, token, handlers) => {
  const { subscribeToChannel, sendMessage, isConnected } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Use refs to store handlers without triggering re-renders
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Use ref for subscribeToChannel to avoid it as dependency
  const subscribeToChannelRef = useRef(subscribeToChannel);
  subscribeToChannelRef.current = subscribeToChannel;

  // Track if we've already subscribed to prevent duplicates
  const hasSubscribedRef = useRef(false);

  useEffect(() => {
    if (!partnerId || !attendantId || !token || !isConnected) {
      if (isSubscribed) {
        setIsSubscribed(false);
        hasSubscribedRef.current = false;
      }
      return;
    }

    // Prevent duplicate subscriptions
    if (hasSubscribedRef.current) {
      return;
    }

    logger.log('Subscribing to AttendantChannel', `partner: ${partnerId}`, `attendant: ${attendantId}`);
    hasSubscribedRef.current = true;

    const unsubscribe = subscribeToChannelRef.current('attendant', { partnerId, attendantId, token }, handlersRef.current);
    setIsSubscribed(true);

    // Defer onConnected call to avoid state updates during render
    setTimeout(() => {
      if (handlersRef.current?.onConnected) {
        handlersRef.current.onConnected();
      }
    }, 0);

    return () => {
      logger.log('Unsubscribing from AttendantChannel');
      unsubscribe();
      setIsSubscribed(false);
      hasSubscribedRef.current = false;
    };
  }, [partnerId, attendantId, token, isConnected]); // Removed subscribeToChannel from dependencies

  const acceptTakeover = useCallback((takeoverId) => {
    return sendMessage('attendant', 'accept_takeover', { takeoverId, attendantId });
  }, [sendMessage, attendantId]);

  const sendAttendantMessage = useCallback((takeoverId, content) => {
    return sendMessage('attendant', 'send_message', { takeoverId, attendantId, content });
  }, [sendMessage, attendantId]);

  return { isSubscribed, isConnected, acceptTakeover, sendAttendantMessage };
};

/**
 * Custom hook for Widget channel
 */
export const useWidgetChannel = (sessionId, handlers) => {
  const { subscribeToChannel, sendMessage, isConnected } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!sessionId || !isConnected) {
      return;
    }

    const unsubscribe = subscribeToChannel('widget', { sessionId }, handlers);
    setIsSubscribed(true);

    return () => {
      unsubscribe();
      setIsSubscribed(false);
    };
  }, [sessionId, isConnected, subscribeToChannel, handlers]);

  // Adicionar método requestTakeover para o widget
  const requestTakeover = useCallback((reason) => {
    return sendMessage('widget', 'request_takeover', { reason });
  }, [sendMessage]);

  // Adicionar método sendWidgetMessage
  const sendWidgetMessage = useCallback((message) => {
    return sendMessage('widget', 'send_message', { message });
  }, [sendMessage]);

  return { isSubscribed, isConnected, requestTakeover, sendWidgetMessage };
};

/**
 * Custom hook for Simulator channel
 */
export const useSimulatorChannel = (partnerId, sessionId, assistantId, handlers) => {
  const { subscribeToChannel, sendMessage, isConnected } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!partnerId || !isConnected) {
      return;
    }

    const unsubscribe = subscribeToChannel('simulator', { partnerId, sessionId, assistantId }, handlers);
    setIsSubscribed(true);

    return () => {
      unsubscribe();
      setIsSubscribed(false);
    };
  }, [partnerId, sessionId, assistantId, isConnected, subscribeToChannel, handlers]);

  const sendSimulatorMessage = useCallback((message) => {
    return sendMessage('simulator', 'send_message', { message });
  }, [sendMessage]);

  const resetConversation = useCallback(() => {
    return sendMessage('simulator', 'reset', {});
  }, [sendMessage]);

  const requestState = useCallback(() => {
    return sendMessage('simulator', 'request_state', {});
  }, [sendMessage]);

  return {
    isSubscribed,
    isConnected,
    sendSimulatorMessage,
    resetConversation,
    requestState
  };
};

/**
 * Custom hook for Flows channel
 * Gerencia o canal de fluxos de automação
 */
export const useFlowsChannel = (partnerId, handlers) => {
  const { subscribeToChannel, sendMessage, isConnected } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    if (!partnerId || !isConnected) {
      return;
    }

    const unsubscribe = subscribeToChannel('flows', { partnerId }, handlers);
    setIsSubscribed(true);

    return () => {
      unsubscribe();
      setIsSubscribed(false);
    };
  }, [partnerId, isConnected, subscribeToChannel, handlers]);

  // Métodos específicos do Flows podem ser adicionados aqui
  const triggerFlow = useCallback((flowId, params) => {
    return sendMessage('flows', 'trigger_flow', { flowId, params });
  }, [sendMessage]);

  const pauseExecution = useCallback((executionId) => {
    return sendMessage('flows', 'pause_execution', { executionId });
  }, [sendMessage]);

  const resumeExecution = useCallback((executionId) => {
    return sendMessage('flows', 'resume_execution', { executionId });
  }, [sendMessage]);

  return {
    isSubscribed,
    isConnected,
    triggerFlow,
    pauseExecution,
    resumeExecution
  };
};